import type { TPlayer } from 'video.js'

import { Events } from '|>/shared/events'
import { bind_ } from '|>/shared/fn'
import { addClass, textContent } from '|>/shared/h'
import { isDolbyTrack } from '|>/shared/utils/dolby'
import { on, register } from '|>/shared/vjs'
import { BaseAudioTrackButton } from '../../../base'
import { LocalEvents } from '../../local-events'
import { normalizeTracksTitles } from '../../normalize-tracks-titles'
import { AudioTrackMenuItem } from '../audio-track-menu-item'

import './audio-track-button.css'

/**
 * Audio track button for settings audio menu
 *
 * This component is actually (in VideoJS) used as a control bar control,
 * which contains a button and a menu with audio tracks.
 * There is no separate menu-only component to select audio tracks.
 * I thought it would be easier to use this component as a base, because
 * it already has a menu and audio track selection logic, but we don't need
 * a button, so, parent component `SettingsAudioMenu` will just use menu
 * part from this component.
 *
 * I also added logic to trigger event on audio track change, I think it's
 * good place for this logic, because this component is already listening
 * to audio track changes.
 *
 * No need to be registered as a component in VideoJS,
 * because it is used directly in `SettingsAudioMenu` component.
 */
@register
export class AudioTrackButton extends BaseAudioTrackButton {
  declare options_: any
  declare menu: NonNullable<InstanceType<typeof BaseAudioTrackButton>['menu']>

  @on(Events.Media.DolbyConfiguration)
  declare dolbyConfig: Events.Media.DolbyConfiguration

  constructor(player: TPlayer, options: any) {
    super(player, options)

    // disable button, this is needed so menu doesn't manage button state
    // and doesn't automatically show/hide itself after menu item is selected
    this.disable()

    // listen to audio track changes
    const tracks = options.tracks
    const updateSelected = bind_(this, this.updateSelected)

    this.handleMenuTitleClick = bind_(this, this.handleMenuTitleClick)
    this.handleMenuAnyTrackClick = bind_(this, this.handleMenuAnyTrackClick)

    tracks.addEventListener('change', updateSelected)
    this.on('dispose', () => {
      tracks.removeEventListener('change', updateSelected)
    })
    this.player_.on(Events.Media.DolbyTracks, () => {
      this.update()
    })
  }

  override update() {
    this.player_.trigger(Events.Media.AudioTracksChanged)
    super.update()

    // adjust menu class
    this.menu.addClass('vjs-x-settings-audio-menu')

    // normalize track titles
    this.normalizeTrackTitles()

    // adjust menu tap/click behavior
    const that = this
    this.menu.handleTapClick = function (event) {
      const items = this.children() // `this` is Menu instance
      const item = items?.find((it) => it.el() === event.target)
      if (item) {
        if (item.hasClass('vjs-menu-title')) {
          that.handleMenuTitleClick()
        } else {
          that.handleMenuAnyTrackClick()
        }
      }
    }
  }

  updateSelected() {
    this.player_.trigger(Events.Media.AudioTrackChanged)
  }

  title(title: string) {
    if (this.menu) {
      const el_ = this.menu.$('.vjs-menu-title') as HTMLElement
      if (el_) {
        textContent(el_, title)
      }
    }
  }

  override createItems(items: any[] = []) {
    // if there's only one audio track, there no point in showing it
    this.hideThreshold_ = 1

    const tracks = this.player_.audioTracks()

    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i]

      let isDolby = false
      const { dolbyPlugin, isHls } = this.player_

      if (this.dolbyConfig?.enabled) {
        isDolby = isDolbyTrack({
          track,
          dolbyTracks: dolbyPlugin?.tracks,
          isHls,
        })
      }

      // cast to any to avoid cross-package Video.js type incompatibilities
      items.push(
        new AudioTrackMenuItem(this.player_, {
          track,
          selectable: true,
          multiSelectable: false,
          className: isDolby ? 'dolby-track' : undefined,
          isDolby,
        }) as any
      )
    }

    return items
  }

  normalizeTrackTitles() {
    if (this.menu) {
      const normalize = normalizeTracksTitles()
      const items = this.menu.$$('.vjs-menu-item-text')
      for (const item of items) {
        const title = normalize(item.textContent)
        if (title != null) {
          textContent(item as HTMLElement, title)
        }
      }
      if (items.length === 1) {
        addClass(items[0] as HTMLElement, 'vjs-selected')
        textContent(items[0] as HTMLElement, this.localize('Default'))
      }
    }
  }

  override handleLanguagechange() {
    this.normalizeTrackTitles()
  }

  handleMenuTitleClick() {
    const container = this.options_.getContainer()
    container?.trigger(LocalEvents.SettingsActivateMainMenu)
  }

  handleMenuAnyTrackClick() {
    const container = this.options_.getContainer()
    container?.trigger(LocalEvents.SettingsCloseMenu)
  }
}
